import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import DpadIcon from "media/icons/DPAD.svg?react";
import { selectIsProcoreMobileDesign } from "slices/explorer";

import {
    markersActions,
    selectAllCollections,
    selectCanDragSelectedMarker,
    selectCreationCollectionId,
    selectIsCreatingMarker,
    selectSelectedMarker,
} from "./markersSlice";
import { CURSOR_DRAGGER_SIZE, PIN_TIP_OFFSET } from "./pinOffsetCursorConstants";
import { useMarkerDragHoverTracking } from "./useMarkerDragHoverTracking";

type CursorMode = "creation" | "edit";

/**
 * Mobile pin placement and edit overlay.
 *
 * Creation mode (isCreatingMarker = true, no selected pin):
 *   DPAD + "Place Pin" label centered on canvas. Drag → pick at pin tip →
 *   updateCreationPreview. Release → addMarker, reset to center.
 *
 * Edit-during-creation (isCreatingMarker = true, pin selected):
 *   DPAD jumps to selected pin. Drag → updateEditPreview. Release → updateMarker,
 *   deselectMarker, DPAD returns to canvas center for next placement.
 *
 * Standalone edit mode (pin selected, !isCreatingMarker):
 *   DPAD on selected pin. Drag → updateEditPreview. Release → updateMarker,
 *   confirmEditLocation. DPAD stays on (possibly moved) pin.
 *   Camera rotation re-projects the pin's world position to keep DPAD aligned.
 */
export function PinOffsetCursor() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const isCreatingMarker = useAppSelector(selectIsCreatingMarker);
    const creationCollectionId = useAppSelector(selectCreationCollectionId);
    const collections = useAppSelector(selectAllCollections);
    const selectedMarker = useAppSelector(selectSelectedMarker);
    const canDragSelectedMarker = useAppSelector(selectCanDragSelectedMarker);
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();
    const { updateHoverObject, clearHoverObject } = useMarkerDragHoverTracking(view);

    const [active, setActive] = useState(false);
    const [cursorMode, setCursorMode] = useState<CursorMode>("creation");
    const cursorModeRef = useRef<CursorMode>("creation");
    cursorModeRef.current = cursorMode;

    const dpadElRef = useRef<HTMLDivElement>(null);
    const labelElRef = useRef<HTMLDivElement>(null);
    // Pin tip in canvas coordinates — the point picks are made at
    const pinTipRef = useRef<{ x: number; y: number } | null>(null);

    // Shared drag state — read by camera update listener to avoid repositioning during drag
    const isDraggingRef = useRef(false);

    // Stable refs so drag effect closures always read fresh values without stale captures
    const selectedMarkerRef = useRef(selectedMarker);
    selectedMarkerRef.current = selectedMarker;
    const collectionsRef = useRef(collections);
    collectionsRef.current = collections;
    const isCreatingMarkerRef = useRef(isCreatingMarker);
    isCreatingMarkerRef.current = isCreatingMarker;
    const creationCollectionIdRef = useRef(creationCollectionId);
    creationCollectionIdRef.current = creationCollectionId;

    /** Positions DPAD and label given the pin tip screen coordinate. */
    const applyLayout = (pinTipX: number, pinTipY: number) => {
        if (dpadElRef.current) {
            const dpadCenterX = pinTipX - PIN_TIP_OFFSET.x;
            const dpadCenterY = pinTipY - PIN_TIP_OFFSET.y;
            dpadElRef.current.style.transform = `translate(${dpadCenterX - CURSOR_DRAGGER_SIZE / 2}px, ${dpadCenterY - CURSOR_DRAGGER_SIZE / 2}px)`;
        }
        if (labelElRef.current) {
            const dpadCenterX = pinTipX - PIN_TIP_OFFSET.x;
            const dpadCenterY = pinTipY - PIN_TIP_OFFSET.y;
            const labelX = dpadCenterX;
            const labelY = dpadCenterY - CURSOR_DRAGGER_SIZE / 2 - 36;
            labelElRef.current.style.transform = `translate(${labelX}px, ${labelY}px)`;
        }
    };

    // Activate creation mode: DPAD at canvas center and prime preview with initial pick.
    // Skips reset when the edit sub-mode has taken over (a pin is selected mid-creation).
    // isMobileDesign guard: component is also mounted in HudProcore so we must not activate there.
    useEffect(() => {
        const canvas = view?.canvas;
        if (!isCreatingMarker || !canvas || !isMobileDesign) {
            if (cursorModeRef.current === "creation") {
                setActive(false);
                pinTipRef.current = null;
            }
            return;
        }

        // If a pin is already selected when creation mode starts, let the edit effect handle it
        if (canDragSelectedMarker && selectedMarker) {
            return;
        }

        const cx = canvas.clientWidth / 2;
        const cy = canvas.clientHeight / 2;
        pinTipRef.current = { x: cx, y: cy };
        setCursorMode("creation");
        setActive(true);

        view.pick(cx, cy)
            .then((r) => {
                if (r?.position) {
                    dispatch(markersActions.updateCreationPreview([r.position[0], r.position[1], r.position[2]]));
                    updateHoverObject(r.objectId);
                }
            })
            .catch(() => {});
    }, [isCreatingMarker, isMobileDesign, canDragSelectedMarker, selectedMarker, view, dispatch, updateHoverObject]);

    // Activate edit mode: project selected marker onto screen and position DPAD there.
    // Works both during creation (edit-during-creation sub-mode) and standalone.
    useEffect(() => {
        if (!canDragSelectedMarker || !selectedMarker || !isMobileDesign || !view) {
            if (cursorModeRef.current === "edit") {
                // Returning to pure creation mode — revert to canvas center
                if (isCreatingMarkerRef.current && view?.canvas) {
                    const canvas = view.canvas;
                    const cx = canvas.clientWidth / 2;
                    const cy = canvas.clientHeight / 2;
                    pinTipRef.current = { x: cx, y: cy };
                    setCursorMode("creation");
                    // active stays true; applyLayout effect fires on cursorMode change
                } else {
                    setActive(false);
                    pinTipRef.current = null;
                }
            }
            return;
        }

        const collection = collections[selectedMarker.collectionId];
        const marker = collection?.markers[selectedMarker.markerId];
        if (!marker) {
            return;
        }

        const screenPts = view.convert.worldSpaceToScreenSpace([marker.position], { round: false });
        const screenPos = screenPts?.[0];
        if (!screenPos) {
            return;
        }

        pinTipRef.current = { x: screenPos[0], y: screenPos[1] };
        setCursorMode("edit");
        setActive(true);

        // Hide the floating creation preview while repositioning an existing pin
        dispatch(markersActions.updateCreationPreview(null));
    }, [canDragSelectedMarker, selectedMarker, isMobileDesign, view, collections, dispatch]);

    // Apply layout whenever the overlay becomes active or switches mode
    useEffect(() => {
        if (active && pinTipRef.current) {
            applyLayout(pinTipRef.current.x, pinTipRef.current.y);
        }
    }, [active, cursorMode]);

    // Re-project the selected pin onto screen whenever the camera moves (edit mode only).
    // Keeps DPAD aligned with the pin as the user rotates/pans the view.
    useEffect(() => {
        if (!active || cursorMode !== "edit" || !view || !internalEventEmitter) {
            return;
        }

        const unsubscribe = internalEventEmitter.on("cameraUpdated", () => {
            if (isDraggingRef.current) {
                return;
            }

            const sm = selectedMarkerRef.current;
            if (!sm) {
                return;
            }

            const currentCollections = collectionsRef.current;
            const collection = currentCollections[sm.collectionId];
            const marker = collection?.markers[sm.markerId];
            if (!marker) {
                return;
            }

            const screenPts = view.convert.worldSpaceToScreenSpace([marker.position], { round: false });
            const screenPos = screenPts?.[0];
            if (!screenPos) {
                return;
            }

            pinTipRef.current = { x: screenPos[0], y: screenPos[1] };
            applyLayout(screenPos[0], screenPos[1]);
        });

        return () => {
            unsubscribe();
        };
    }, [active, cursorMode, view, internalEventEmitter]);

    // DPAD drag: fire-and-forget picks, dispatch preview updates, commit on release
    useEffect(() => {
        const dpad = dpadElRef.current;
        if (!active || !dpad || !view) {
            return;
        }

        let cancelled = false;
        let captureOffset: { x: number; y: number } | null = null;
        let isDragging = false;
        let isPicking = false;
        let hasStartedEdit = false;

        const getCanvasPos = (e: PointerEvent) => {
            const rect = view.canvas!.getBoundingClientRect();
            return { x: e.clientX - rect.left, y: e.clientY - rect.top };
        };

        // Toggle DPAD visual: green stroke ring + hidden arrows while dragging.
        // Targets elements by data-role to avoid fragile positional index assumptions.
        const setDragVisual = (dragging: boolean) => {
            const svg = dpad.querySelector("svg");
            if (!svg) {
                return;
            }
            const strokeRing = svg.querySelector<SVGCircleElement>('[data-role="stroke-ring"]');
            if (strokeRing) {
                strokeRing.style.stroke = dragging ? "#22C55E" : "";
                strokeRing.style.strokeWidth = dragging ? "2" : "";
            }
            svg.querySelectorAll("path").forEach((p) => {
                (p as SVGPathElement).style.opacity = dragging ? "0" : "";
            });
            const centerDot = svg.querySelector<SVGCircleElement>('[data-role="center-dot"]');
            if (centerDot) {
                centerDot.style.opacity = dragging ? "0" : "";
            }
        };

        const onPointerDown = (e: PointerEvent) => {
            dpad.setPointerCapture(e.pointerId);
            const pointer = getCanvasPos(e);
            const currentPin = pinTipRef.current!;
            const dpadCX = currentPin.x - PIN_TIP_OFFSET.x;
            const dpadCY = currentPin.y - PIN_TIP_OFFSET.y;
            captureOffset = { x: dpadCX - pointer.x, y: dpadCY - pointer.y };
            isDragging = true;
            isDraggingRef.current = true;
            hasStartedEdit = false;
            setDragVisual(true);
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!isDragging || !captureOffset) {
                return;
            }

            const pointer = getCanvasPos(e);
            const dpadCX = pointer.x + captureOffset.x;
            const dpadCY = pointer.y + captureOffset.y;
            const pinTipX = dpadCX + PIN_TIP_OFFSET.x;
            const pinTipY = dpadCY + PIN_TIP_OFFSET.y;

            pinTipRef.current = { x: pinTipX, y: pinTipY };
            applyLayout(pinTipX, pinTipY);

            // Dispatch startEditLocation on first motion in edit mode (creation or standalone)
            if (cursorModeRef.current === "edit" && !hasStartedEdit) {
                hasStartedEdit = true;
                const sm = selectedMarkerRef.current;
                if (sm) {
                    dispatch(
                        markersActions.startEditLocation({
                            collectionId: sm.collectionId,
                            markerId: sm.markerId,
                        }),
                    );
                }
            }

            if (!isPicking) {
                isPicking = true;
                view.pick(pinTipX, pinTipY)
                    .then((r) => {
                        if (cancelled) {
                            return;
                        }
                        if (r?.position) {
                            if (cursorModeRef.current === "creation") {
                                dispatch(
                                    markersActions.updateCreationPreview([r.position[0], r.position[1], r.position[2]]),
                                );
                            } else {
                                dispatch(
                                    markersActions.updateEditPreview([r.position[0], r.position[1], r.position[2]]),
                                );
                            }
                            updateHoverObject(r.objectId);
                        } else {
                            if (cursorModeRef.current === "creation") {
                                dispatch(markersActions.updateCreationPreview(null));
                            }
                            updateHoverObject(undefined);
                        }
                    })
                    .catch(() => {})
                    .finally(() => {
                        isPicking = false;
                    });
            }
        };

        const onPointerUp = async () => {
            if (!isDragging) {
                return;
            }
            isDragging = false;
            isDraggingRef.current = false;
            captureOffset = null;
            setDragVisual(false);
            clearHoverObject();

            if (cursorModeRef.current === "creation") {
                dispatch(markersActions.updateCreationPreview(null));

                const currentCreationCollectionId = creationCollectionIdRef.current;
                if (!currentCreationCollectionId) {
                    return;
                }

                const pinTip = pinTipRef.current!;
                try {
                    const r = await view.pick(pinTip.x, pinTip.y);
                    if (cancelled) {
                        return;
                    }
                    if (r?.position) {
                        const position: [number, number, number] = [r.position[0], r.position[1], r.position[2]];
                        const currentCollections = collectionsRef.current;
                        const creationConfig =
                            currentCollections[currentCreationCollectionId]?.markerCreationConfig || {};
                        const generateId = creationConfig.generateId ?? (() => globalThis.crypto.randomUUID());
                        const markerId = generateId();
                        dispatch(
                            markersActions.addMarker({
                                collectionId: currentCreationCollectionId,
                                marker: {
                                    id: markerId,
                                    position,
                                    collectionId: currentCreationCollectionId,
                                    metadata: {},
                                },
                            }),
                        );
                        creationConfig.onCreated?.({ id: markerId, position });
                    }
                } catch {
                    /* swallow */
                }
                if (cancelled) {
                    return;
                }

                // Reset to canvas center; re-prime preview there
                const canvas = view.canvas;
                if (canvas) {
                    const cx = canvas.clientWidth / 2;
                    const cy = canvas.clientHeight / 2;
                    pinTipRef.current = { x: cx, y: cy };
                    applyLayout(cx, cy);

                    view.pick(cx, cy)
                        .then((r) => {
                            if (r?.position) {
                                dispatch(
                                    markersActions.updateCreationPreview([r.position[0], r.position[1], r.position[2]]),
                                );
                                updateHoverObject(r.objectId);
                            }
                        })
                        .catch(() => {});
                }
            } else {
                // Edit mode (standalone or during creation)
                if (!hasStartedEdit) {
                    return;
                }

                const sm = selectedMarkerRef.current;
                if (!sm) {
                    dispatch(markersActions.cancelEditLocation());
                    return;
                }

                const pinTip = pinTipRef.current!;
                try {
                    const r = await view.pick(pinTip.x, pinTip.y);
                    if (cancelled) {
                        return;
                    }
                    if (r?.position) {
                        const position: [number, number, number] = [r.position[0], r.position[1], r.position[2]];
                        const currentCollections = collectionsRef.current;
                        const collection = currentCollections[sm.collectionId];
                        const markerBefore = collection?.markers[sm.markerId];

                        dispatch(
                            markersActions.updateMarker({
                                collectionId: sm.collectionId,
                                markerId: sm.markerId,
                                updates: { position },
                            }),
                        );

                        if (collection && markerBefore) {
                            const updatedMarker = { ...markerBefore, position };
                            collection.config?.onLocationUpdated?.(updatedMarker, markerBefore.position);
                        }
                    }
                } catch {
                    /* swallow */
                }

                if (cancelled) {
                    return;
                }
                dispatch(markersActions.confirmEditLocation());

                // If editing a pin during creation mode, deselect it and return DPAD to center
                if (isCreatingMarkerRef.current) {
                    dispatch(markersActions.deselectMarker());

                    const canvas = view.canvas;
                    if (canvas) {
                        const cx = canvas.clientWidth / 2;
                        const cy = canvas.clientHeight / 2;
                        pinTipRef.current = { x: cx, y: cy };
                        // cursorMode will switch to "creation" via the edit effect cleanup,
                        // which triggers the layout effect — applyLayout will be called then
                    }
                }
            }
        };

        dpad.addEventListener("pointerdown", onPointerDown);
        dpad.addEventListener("pointermove", onPointerMove);
        dpad.addEventListener("pointerup", onPointerUp);
        dpad.addEventListener("pointercancel", onPointerUp);

        return () => {
            cancelled = true;
            isDraggingRef.current = false;
            setDragVisual(false);
            dpad.removeEventListener("pointerdown", onPointerDown);
            dpad.removeEventListener("pointermove", onPointerMove);
            dpad.removeEventListener("pointerup", onPointerUp);
            dpad.removeEventListener("pointercancel", onPointerUp);
        };
    }, [active, view, dispatch, updateHoverObject, clearHoverObject]);

    if (!active) {
        return null;
    }

    const labelText = cursorMode === "edit" ? t("movePin") : t("pins");

    return (
        <Box sx={{ inset: 0, overflow: "hidden", pointerEvents: "none", position: "absolute" }}>
            {/* Label — positioned above the DPAD center */}
            <Box ref={labelElRef} sx={{ left: 0, position: "absolute", top: 0 }}>
                <Box
                    sx={{
                        backgroundColor: "rgba(32, 102, 223, 0.8)",
                        borderRadius: "100px",
                        boxShadow: "0px 2px 3px rgba(0,0,0,0.1)",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: 600,
                        letterSpacing: "0.25px",
                        lineHeight: "16px",
                        px: "12px",
                        py: "8px",
                        whiteSpace: "nowrap",
                    }}
                >
                    {labelText}
                </Box>
            </Box>

            {/* DPAD — centered at (pinTip - PIN_TIP_OFFSET), i.e. at finger during drag */}
            <Box
                ref={dpadElRef}
                sx={{
                    cursor: "grab",
                    filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.20))",
                    left: 0,
                    pointerEvents: "all",
                    position: "absolute",
                    top: 0,
                    touchAction: "none",
                    userSelect: "none",
                }}
            >
                <DpadIcon width={CURSOR_DRAGGER_SIZE} height={CURSOR_DRAGGER_SIZE} />
            </Box>
        </Box>
    );
}
